import CloseIcon from "@mui/icons-material/Close"
import {
    Divider,
    IconButton,
    InputAdornment,
    InputBase,
    List,
    ListItem,
    ListSubheader,
    Stack,
    Typography,
} from "@mui/material"
import { Dispatch, ReactElement, SetStateAction, useEffect } from "react"
import { useNavigate } from "react-router-dom"

import AppSearchOption from "./appSearchOption"
import QuickAction from "./quickAction"
import { useGetQuickActions } from "./useGetQuickActions"
import { LCTOnlyDatasets } from "../../constants/enums"
import { DNO_REVIEWER, INSTALLER } from "../../constants/roles"
import {
    DNO_SINGLE_APPLICATION_BASE,
    INSTALLER_SINGLE_APPLICATION_BASE,
    SINGLE_DEMAND_DEVICE_NAV_LINK,
    SINGLE_GEN_DEVICE_NAV_LINK,
} from "../../router/routes"
import AppButton from "../buttons/appButton"
import AppDialog from "../dialog/appDialog"
import AppTooltip from "../helper/appTooltip"
import {
    ApprovalsIcon,
    EVChargerIcon,
    HeatPumpIcon,
    ElectricBoilerIcon,
    AirConditioningIcon,
    SolarPanelIcon,
} from "../icons/icons"

interface ISearchPanelProps {
    open: boolean
    setOpen: Dispatch<SetStateAction<boolean>>
    search: string
    setSearch: Dispatch<SetStateAction<string>>
}

/**
 * Search panel for the application. This is a dialog that opens when the user
 * presses ⌘K. Different search options are presented to the user based on the
 * roles they have
 * @returns
 */
const SearchPanel = ({
    open,
    setOpen,
    search,
    setSearch,
}: ISearchPanelProps): ReactElement => {
    const navigate = useNavigate()
    const quickActions = useGetQuickActions(search)

    // Toggle the menu when ⌘K is pressed
    useEffect(() => {
        const down = (e: KeyboardEvent): void => {
            if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                setOpen((prevOpen) => !prevOpen)
            }
        }

        document.addEventListener("keydown", down)
        return () => document.removeEventListener("keydown", down)
    }, [setOpen])

    return (
        <AppDialog isOpen={open} sx={{ p: 0 }} maxWidth="md">
            <InputBase
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search Connect Direct..."
                sx={{ px: 1.5, py: 1 }}
                autoFocus
                endAdornment={
                    search && (
                        <InputAdornment position="end">
                            <AppTooltip title="Clear search">
                                <IconButton
                                    edge="end"
                                    onClick={() => setSearch("")}
                                    sx={{ pr: 2 }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </AppTooltip>
                        </InputAdornment>
                    )
                }
            />
            <Divider />
            <List
                component="nav"
                subheader={
                    <ListSubheader component="div">
                        Quick Start Actions
                    </ListSubheader>
                }
            >
                {quickActions.length > 0 ? (
                    quickActions.map((action) => (
                        <QuickAction
                            key={action.title}
                            title={action.title}
                            icon={action.icon}
                            setOpen={setOpen}
                            onClick={action.onClick}
                        />
                    ))
                ) : (
                    <ListItem>
                        <Typography variant="body1" color="textSecondary">
                            There are no quick actions matching your search
                        </Typography>
                    </ListItem>
                )}
            </List>
            <List
                component="nav"
                subheader={
                    <ListSubheader component="div">Quick Search</ListSubheader>
                }
            >
                <AppSearchOption
                    title="Search for an application"
                    search={search}
                    icon={<ApprovalsIcon />}
                    setOpen={setOpen}
                    displayRoles={[DNO_REVIEWER]}
                    onClick={() =>
                        navigate(`${DNO_SINGLE_APPLICATION_BASE}/${search}`)
                    }
                />
                <AppSearchOption
                    title="Search for an application"
                    search={search}
                    icon={<ApprovalsIcon />}
                    setOpen={setOpen}
                    displayRoles={[INSTALLER]}
                    onClick={() =>
                        navigate(
                            `${INSTALLER_SINGLE_APPLICATION_BASE}/${search}`
                        )
                    }
                />
                <AppSearchOption
                    title="Search for a heat pump with system reference"
                    search={search}
                    icon={<HeatPumpIcon />}
                    setOpen={setOpen}
                    onClick={() =>
                        navigate(
                            SINGLE_DEMAND_DEVICE_NAV_LINK(
                                LCTOnlyDatasets.HP,
                                search
                            )
                        )
                    }
                />
                <AppSearchOption
                    title="Search for an electric boiler with system reference"
                    search={search}
                    icon={<ElectricBoilerIcon />}
                    setOpen={setOpen}
                    onClick={() =>
                        navigate(
                            SINGLE_DEMAND_DEVICE_NAV_LINK(
                                LCTOnlyDatasets.EB,
                                search
                            )
                        )
                    }
                />
                <AppSearchOption
                    title="Search for an air conditioning unit with system reference"
                    search={search}
                    icon={<AirConditioningIcon />}
                    setOpen={setOpen}
                    onClick={() =>
                        navigate(
                            SINGLE_DEMAND_DEVICE_NAV_LINK(
                                LCTOnlyDatasets.ACU,
                                search
                            )
                        )
                    }
                />
                <AppSearchOption
                    title="Search for an EV AC Charger with system reference"
                    search={search}
                    icon={<EVChargerIcon />}
                    setOpen={setOpen}
                    onClick={() =>
                        navigate(
                            SINGLE_DEMAND_DEVICE_NAV_LINK(
                                LCTOnlyDatasets.EVAC,
                                search
                            )
                        )
                    }
                />
                <AppSearchOption
                    title="Search for an EV DC Charger with system reference"
                    search={search}
                    icon={<EVChargerIcon />}
                    setOpen={setOpen}
                    onClick={() =>
                        navigate(
                            SINGLE_DEMAND_DEVICE_NAV_LINK(
                                LCTOnlyDatasets.EVDC,
                                search
                            )
                        )
                    }
                />
                <AppSearchOption
                    title="Search for an inverter with system reference"
                    search={search}
                    icon={<SolarPanelIcon />}
                    setOpen={setOpen}
                    onClick={() => navigate(SINGLE_GEN_DEVICE_NAV_LINK(search))}
                />
            </List>

            <Divider />
            <Stack
                justifyContent="space-between"
                alignItems="center"
                padding={1}
                direction="row"
                width="100%"
            >
                <Typography variant="caption">
                    You can press ⌘K or CTRL+K to launch this search panel!
                </Typography>
                <AppButton size="small" onClick={() => setOpen(false)}>
                    Close
                </AppButton>
            </Stack>
        </AppDialog>
    )
}

export default SearchPanel
